/**
 * WeeklyContentManager - Cetalingua FM
 * Target path: /admin-panel/src/components/advertiser/WeeklyContentManager.js
 * REPLACES previous version
 *
 * Changes from previous:
 * - Shows submitted content with full media preview (images/videos)
 * - App-style preview panel mimicking what users see in CompanyDetailScreen
 * - Delete button on all content (no edit — once submitted, locked)
 * - Better error handling for storage/index issues
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, getDocs, addDoc, deleteDoc,
  doc, Timestamp, orderBy, limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase-config';
import {
  CONTENT_TYPES, CADENCE, LIMITS, STORAGE_PATHS,
  getCurrentPeriod, getNextPeriodDate, getCadenceForTier
} from '../../shared/advertiserConstants';
import { getSpeciesSummary } from '../../shared/speciesUtils';
import './WeeklyContentManager.css';

const WeeklyContentManager = ({ userId, userData }) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null); // contentId being deleted
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // item to show in app preview

  // Which content type form is open
  const [activeForm, setActiveForm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    mediaFile: null,
    mediaPreview: null,
    mediaType: null,
    dealCode: '',
    dealDiscount: '',
    dealUrl: '',
    dealExpiry: '',
  });

  // Cadence info
  // Stage 3: "paid" = a Stripe subscription that includes Advertiser (sticker program = free tier)
  const paidTier = ['active', 'trialing', 'past_due'].includes(userData?.portalSubscription?.status)
    && (userData?.portalSubscription?.products || []).includes('advertiser');
  const cadence = getCadenceForTier(paidTier ? 'paid' : 'free');
  const currentPeriod = getCurrentPeriod(cadence);
  const nextPeriodDate = getNextPeriodDate(cadence);

  // Track which types already have content this period
  const [periodUsage, setPeriodUsage] = useState({
    featured_media: false,
    whats_new: false,
    hot_deal: false,
  });

  // Fetch existing content
  const fetchContent = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'advertiserContent'),
        where('advertiserId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(30)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setContentItems(items);

      // Check period usage
      const usage = { featured_media: false, whats_new: false, hot_deal: false };
      items.forEach(item => {
        if (item.publishPeriod === currentPeriod && item.status !== 'rejected') {
          usage[item.type] = true;
        }
      });
      setPeriodUsage(usage);
    } catch (error) {
      console.error('Error fetching content:', error);
      if (error.message?.includes('index')) {
        setMessage({
          type: 'error',
          text: 'Database index not ready yet. Click the link in your browser console to create it, then refresh.'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [userId, currentPeriod]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '', body: '', mediaFile: null, mediaPreview: null, mediaType: null,
      dealCode: '', dealDiscount: '', dealUrl: '', dealExpiry: '',
    });
    setActiveForm(null);
    setMessage({ type: '', text: '' });
  };

  // Handle media file selection
  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      setMessage({ type: 'error', text: 'Only image and video files are allowed' });
      return;
    }
    if (file.size > LIMITS.MAX_MEDIA_SIZE_MB * 1024 * 1024) {
      setMessage({ type: 'error', text: `File must be under ${LIMITS.MAX_MEDIA_SIZE_MB}MB` });
      return;
    }

    const preview = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      mediaFile: file,
      mediaPreview: preview,
      mediaType: isVideo ? 'video' : 'image',
    }));
  };

  // Upload media to Firebase Storage
  const uploadMedia = async (file) => {
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = STORAGE_PATHS.ADVERTISER_CONTENT(userId, `${timestamp}_${safeName}`);
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Submit content
  const handleSubmit = async (status = 'pending') => {
    setMessage({ type: '', text: '' });

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }
    if (formData.title.length > LIMITS.TITLE_MAX) {
      setMessage({ type: 'error', text: `Title must be ${LIMITS.TITLE_MAX} characters or less` });
      return;
    }

    const typeConfig = CONTENT_TYPES.find(t => t.id === activeForm);

    if (typeConfig?.requiresBody && !formData.body.trim()) {
      setMessage({ type: 'error', text: 'Description is required for this content type' });
      return;
    }
    if (typeConfig?.requiresMedia && !formData.mediaFile) {
      setMessage({ type: 'error', text: 'Please upload an image or video' });
      return;
    }

    if (activeForm === 'hot_deal' && !formData.dealDiscount.trim()) {
      setMessage({ type: 'error', text: 'Deal discount/offer description is required' });
      return;
    }

    setSubmitting(true);

    try {
      let mediaUrl = null;

      if (formData.mediaFile) {
        setUploadingMedia(true);
        try {
          mediaUrl = await uploadMedia(formData.mediaFile);
        } catch (storageErr) {
          console.error('Storage upload error:', storageErr);
          if (storageErr.code === 'storage/unauthorized') {
            setMessage({
              type: 'error',
              text: 'Upload blocked — Firebase Storage rules need to be deployed. Run: firebase deploy --only storage'
            });
          } else {
            setMessage({ type: 'error', text: `Upload failed: ${storageErr.message}` });
          }
          setSubmitting(false);
          setUploadingMedia(false);
          return;
        }
        setUploadingMedia(false);
      }

      const contentDoc = {
        type: activeForm,
        advertiserId: userId,
        advertiserName: userData.companyName || 'Unknown',
        advertiserEmail: userData.email || '',

        title: formData.title.trim(),
        body: formData.body.trim() || null,
        mediaUrl: mediaUrl,
        mediaType: formData.mediaType,
        thumbnailUrl: null,

        // Hot deal fields
        dealCode: activeForm === 'hot_deal' ? (formData.dealCode.trim() || null) : null,
        dealDiscount: activeForm === 'hot_deal' ? (formData.dealDiscount.trim() || null) : null,
        dealUrl: activeForm === 'hot_deal' ? (formData.dealUrl.trim() || null) : null,
        dealExpiry: activeForm === 'hot_deal' && formData.dealExpiry
          ? Timestamp.fromDate(new Date(formData.dealExpiry)) : null,

        // Targeting
        species: userData.species || userData.speciesInterests || [],

        // Status
        status: status,
        rejectionReason: null,
        isActive: true,

        // Cadence
        cadence: cadence,
        publishPeriod: currentPeriod,

        // Timestamps
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        submittedAt: status === 'pending' ? Timestamp.now() : null,
        approvedAt: null,
        approvedBy: null,
        publishedAt: null,
        expiresAt: null,
        rejectedAt: null,
        rejectedBy: null,
      };

      await addDoc(collection(db, 'advertiserContent'), contentDoc);

      setMessage({
        type: 'success',
        text: status === 'pending'
          ? 'Content submitted for admin approval!'
          : 'Draft saved successfully!'
      });

      resetForm();
      fetchContent();
    } catch (error) {
      console.error('Error submitting content:', error);
      setMessage({ type: 'error', text: `Failed to submit: ${error.message}` });
    } finally {
      setSubmitting(false);
      setUploadingMedia(false);
    }
  };

  // Delete content (any status)
  const handleDelete = async (item) => {
    const statusLabel = item.status === 'approved' ? 'This content is live and visible to users. ' : '';
    if (!window.confirm(`${statusLabel}Delete "${item.title}"? This cannot be undone.`)) return;

    setDeleting(item.id);
    try {
      // Try to delete media from storage too
      if (item.mediaUrl) {
        try {
          const mediaRef = ref(storage, item.mediaUrl);
          await deleteObject(mediaRef);
        } catch (storageErr) {
          console.warn('Could not delete media file:', storageErr);
          // Continue with doc delete even if storage delete fails
        }
      }
      await deleteDoc(doc(db, 'advertiserContent', item.id));
      setMessage({ type: 'success', text: 'Content deleted' });
      if (previewItem?.id === item.id) setPreviewItem(null);
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      setMessage({ type: 'error', text: `Delete failed: ${error.message}` });
    } finally {
      setDeleting(null);
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format countdown
  const getCountdownText = () => {
    const now = new Date();
    const diff = nextPeriodDate - now;
    if (diff <= 0) return 'Available now!';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h until next ${cadence === 'weekly' ? 'week' : 'month'}`;
    return `${hours}h until next ${cadence === 'weekly' ? 'week' : 'month'}`;
  };

  // Status badge
  const getStatusBadge = (status) => {
    const badges = {
      draft: { emoji: '📝', label: 'Draft', cls: 'badge-draft' },
      pending: { emoji: '⏳', label: 'Pending Review', cls: 'badge-pending' },
      approved: { emoji: '✅', label: 'Live', cls: 'badge-approved' },
      rejected: { emoji: '❌', label: 'Rejected', cls: 'badge-rejected' },
      expired: { emoji: '⌛', label: 'Expired', cls: 'badge-expired' },
    };
    const b = badges[status] || badges.draft;
    return <span className={`content-badge ${b.cls}`}>{b.emoji} {b.label}</span>;
  };

  const allSlotsUsed = periodUsage.featured_media && periodUsage.whats_new && periodUsage.hot_deal;

  // =========================================================================
  // APP PREVIEW — mimics CompanyDetailScreen from the mobile app
  // =========================================================================
  const renderAppPreview = (item) => {
    const typeInfo = CONTENT_TYPES.find(t => t.id === item.type);
    return (
      <div className="app-preview-overlay" onClick={() => setPreviewItem(null)}>
        <div className="app-preview-container" onClick={(e) => e.stopPropagation()}>
          <div className="app-preview-header">
            <span>Preview: How users see this in the app</span>
            <button onClick={() => setPreviewItem(null)}>✕</button>
          </div>

          {/* Phone frame */}
          <div className="phone-frame">
            <div className="phone-notch"></div>
            <div className="phone-screen">
              {/* Status bar */}
              <div className="phone-status-bar">
                <span>9:41</span>
                <span>●●●</span>
              </div>

              {/* Company header */}
              <div className="phone-company-header">
                <div className="phone-company-avatar">
                  {(userData?.companyName || 'C')[0].toUpperCase()}
                </div>
                <div>
                  <div className="phone-company-name">{userData?.companyName || 'Your Company'}</div>
                  <div className="phone-company-loc">
                    {userData?.location?.city || 'Location'}{userData?.location?.state ? `, ${userData.location.state}` : ''}
                  </div>
                </div>
              </div>

              {/* Content card — this is what they see */}
              <div className="phone-content-section">
                <div className="phone-section-label">
                  {typeInfo?.emoji} {typeInfo?.name}
                </div>

                <div className={`phone-content-card phone-type-${item.type}`}>
                  {/* Media */}
                  {item.mediaUrl && (
                    <div className="phone-media">
                      {item.mediaType === 'video' ? (
                        <video src={item.mediaUrl} controls playsInline />
                      ) : (
                        <img src={item.mediaUrl} alt={item.title} />
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <div className="phone-content-title">{item.title}</div>

                  {/* Body */}
                  {item.body && (
                    <div className="phone-content-body">{item.body}</div>
                  )}

                  {/* Hot deal extras */}
                  {item.type === 'hot_deal' && (
                    <div className="phone-deal-section">
                      {item.dealDiscount && (
                        <div className="phone-deal-badge">{item.dealDiscount}</div>
                      )}
                      {item.dealCode && (
                        <div className="phone-deal-code">
                          Code: <strong>{item.dealCode}</strong>
                        </div>
                      )}
                      {item.dealUrl && (
                        <div className="phone-deal-btn">Book Now</div>
                      )}
                      {item.dealExpiry && (
                        <div className="phone-deal-expiry">
                          Expires {formatDate(item.dealExpiry)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="phone-content-date">{formatDate(item.createdAt)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // CONTENT CARD — shows submitted content with media
  // =========================================================================
  const renderContentCard = (item) => {
    const typeInfo = CONTENT_TYPES.find(t => t.id === item.type);
    const isDeleting = deleting === item.id;

    return (
      <div key={item.id} className={`submitted-card status-${item.status}`}>
        {/* Card header */}
        <div className="sc-header">
          <div className="sc-type">
            <span className="sc-emoji">{typeInfo?.emoji || '📄'}</span>
            <span className="sc-type-name">{typeInfo?.name}</span>
            {getStatusBadge(item.status)}
          </div>
          <div className="sc-date">{formatDate(item.createdAt)}</div>
        </div>

        {/* Media + Content */}
        <div className="sc-body">
          {item.mediaUrl && (
            <div className="sc-media">
              {item.mediaType === 'video' ? (
                <video src={item.mediaUrl} controls playsInline />
              ) : (
                <img src={item.mediaUrl} alt={item.title} />
              )}
            </div>
          )}

          <div className="sc-content">
            <div className="sc-title">{item.title}</div>
            {item.body && <div className="sc-text">{item.body}</div>}

            {/* Deal info */}
            {item.type === 'hot_deal' && (
              <div className="sc-deal-info">
                {item.dealDiscount && <span className="sc-deal-tag">{item.dealDiscount}</span>}
                {item.dealCode && <span className="sc-deal-code">Code: {item.dealCode}</span>}
                {item.dealExpiry && <span className="sc-deal-expiry">Expires {formatDate(item.dealExpiry)}</span>}
                {item.dealUrl && <a href={item.dealUrl} target="_blank" rel="noopener noreferrer" className="sc-deal-link">Booking link</a>}
              </div>
            )}

            {/* Rejection reason */}
            {item.status === 'rejected' && item.rejectionReason && (
              <div className="sc-rejection">
                Rejection reason: {item.rejectionReason}
              </div>
            )}
          </div>
        </div>

        {/* Card actions */}
        <div className="sc-actions">
          <button
            className="sc-preview-btn"
            onClick={() => setPreviewItem(item)}
          >
            📱 App Preview
          </button>
          <button
            className="sc-delete-btn"
            onClick={() => handleDelete(item)}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : '🗑️ Delete'}
          </button>
        </div>

        {/* Period tag */}
        <div className="sc-period">{item.publishPeriod}</div>
      </div>
    );
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="weekly-content-manager">
      <div className="section-header">
        <h2>{cadence === 'weekly' ? '📅 Weekly Content' : '📅 Monthly Content'}</h2>
        <p>Create content that appears on your company page in the Cetalingua FM app</p>
      </div>

      {/* Cadence Info Bar */}
      <div className="cadence-bar">
        <div className="cadence-info">
          <span className="cadence-label">
            {cadence === 'weekly' ? 'Weekly' : 'Monthly'} posting period:
          </span>
          <span className="cadence-period">{currentPeriod}</span>
        </div>
        {allSlotsUsed && (
          <div className="cadence-countdown">
            ⏳ {getCountdownText()}
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Content Type Cards — slot selection */}
      {!activeForm && (
        <div className="content-type-grid">
          {CONTENT_TYPES.map(type => {
            const used = periodUsage[type.id];
            return (
              <div key={type.id} className={`content-type-card ${used ? 'used' : ''}`}>
                <div className="ctc-header">
                  <span className="ctc-emoji">{type.emoji}</span>
                  <h3>{type.name}</h3>
                </div>
                <p className="ctc-desc">{type.desc}</p>
                {used ? (
                  <div className="ctc-used">✅ Posted this {cadence === 'weekly' ? 'week' : 'month'}</div>
                ) : (
                  <button
                    className="ctc-create-btn"
                    onClick={() => setActiveForm(type.id)}
                  >
                    + Create {type.name}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================================= */}
      {/* Content Creation Form                                          */}
      {/* ============================================================= */}
      {activeForm && (
        <div className="content-form">
          <div className="content-form-header">
            <h3>
              {CONTENT_TYPES.find(t => t.id === activeForm)?.emoji}{' '}
              Create {CONTENT_TYPES.find(t => t.id === activeForm)?.name}
            </h3>
            <button className="cancel-btn" onClick={resetForm}>✕ Cancel</button>
          </div>

          {/* Title */}
          <div className="form-group">
            <label>
              Title *
              <span className="char-count">{formData.title.length}/{LIMITS.TITLE_MAX}</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder={
                activeForm === 'featured_media' ? 'Stunning Humpback Breach!' :
                activeForm === 'whats_new' ? 'New Boat Added to Our Fleet' :
                'Spring Special: 15% Off All Tours'
              }
              maxLength={LIMITS.TITLE_MAX}
            />
          </div>

          {/* Media upload */}
          {(activeForm === 'featured_media' || activeForm === 'whats_new') && (
            <div className="form-group">
              <label>
                {activeForm === 'featured_media' ? 'Image or Video *' : 'Image (optional)'}
              </label>
              <div className="media-upload-area">
                {formData.mediaPreview ? (
                  <div className="media-preview">
                    {formData.mediaType === 'video' ? (
                      <video src={formData.mediaPreview} controls style={{ maxWidth: '100%', maxHeight: 200 }} />
                    ) : (
                      <img src={formData.mediaPreview} alt="Preview" />
                    )}
                    <button className="remove-media" onClick={() => setFormData(prev => ({
                      ...prev, mediaFile: null, mediaPreview: null, mediaType: null
                    }))}>✕ Remove</button>
                  </div>
                ) : (
                  <label className="upload-dropzone">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleMediaSelect}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-icon">📤</div>
                    <div className="upload-text">Click to upload image or video</div>
                    <div className="upload-hint">Max {LIMITS.MAX_MEDIA_SIZE_MB}MB — JPG, PNG, MP4</div>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Body/Description */}
          {(activeForm === 'whats_new' || activeForm === 'hot_deal') && (
            <div className="form-group">
              <label>
                Description {activeForm !== 'featured_media' ? '*' : ''}
                <span className="char-count">{formData.body.length}/{LIMITS.BODY_MAX}</span>
              </label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                placeholder={
                  activeForm === 'whats_new'
                    ? 'Share your latest company news or update...'
                    : 'Describe the deal and what app users will get...'
                }
                maxLength={LIMITS.BODY_MAX}
                rows={4}
              />
            </div>
          )}

          {/* Hot Deal fields */}
          {activeForm === 'hot_deal' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount/Offer *</label>
                  <input
                    type="text"
                    value={formData.dealDiscount}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealDiscount: e.target.value }))}
                    placeholder="15% off, $20 off, BOGO, etc."
                    maxLength={LIMITS.DEAL_DISCOUNT_MAX}
                  />
                </div>
                <div className="form-group">
                  <label>Promo Code (optional)</label>
                  <input
                    type="text"
                    value={formData.dealCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealCode: e.target.value.toUpperCase() }))}
                    placeholder="WHALE15"
                    maxLength={LIMITS.DEAL_CODE_MAX}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Booking/Redemption URL (optional)</label>
                  <input
                    type="url"
                    value={formData.dealUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealUrl: e.target.value }))}
                    placeholder="https://yoursite.com/book"
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Date (optional)</label>
                  <input
                    type="date"
                    value={formData.dealExpiry}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealExpiry: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </>
          )}

          {/* Targeting info */}
          <div className="targeting-info">
            <span className="targeting-icon">🎯</span>
            <span>
              This content will be shown to users interested in:{' '}
              <strong>{getSpeciesSummary(userData?.species || userData?.speciesInterests || [], 3)}</strong>
            </span>
          </div>

          {/* Form actions */}
          <div className="form-actions">
            <button
              className="draft-btn"
              onClick={() => handleSubmit('draft')}
              disabled={submitting}
            >
              💾 Save Draft
            </button>
            <button
              className="submit-btn"
              onClick={() => handleSubmit('pending')}
              disabled={submitting}
            >
              {submitting
                ? (uploadingMedia ? 'Uploading media...' : 'Submitting...')
                : '📤 Submit for Approval'}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* Submitted Content — cards with media preview + delete          */}
      {/* ============================================================= */}
      {contentItems.length > 0 && (
        <div className="submitted-content">
          <h3>Your Submitted Content</h3>
          <div className="submitted-grid">
            {contentItems.map(item => renderContentCard(item))}
          </div>
        </div>
      )}

      {contentItems.length === 0 && !loading && !activeForm && (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <p>No content yet. Use the cards above to create your first post!</p>
        </div>
      )}

      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your content...</p>
        </div>
      )}

      {/* App Preview Modal */}
      {previewItem && renderAppPreview(previewItem)}
    </div>
  );
};

export default WeeklyContentManager;
